<template>
  <v-container fluid>
    <v-layout row wrap mb-1>
      <v-flex xs12 sm6 pa-2 pl-0>
        <v-card>
          <HeaderBar size="headline">
            Fulfillment PAST 28 Days
            <template slot="buttons">
              <span class="subtle">
                {{ fulfillmentPastStart | moment('MMM DD') }} to {{
                fulfillmentPastEnd | moment('MMM DD') }}
              </span>
            </template>
          </HeaderBar>
          <v-card-text v-if="gettingFulfillment">
            <v-progress-linear indeterminate />
          </v-card-text>
          <v-card-text v-else>
            <div>
              <BarChart :chart-data="pastFulfillment" :options="chartOptions" />
            </div>
          </v-card-text>
        </v-card>
      </v-flex>

      <v-flex xs12 sm6 pa-2 pr-0>
        <v-card>
          <HeaderBar size="headline">
            Fulfillment UPCOMING 28 Days
            <template slot="buttons">
              <span class="subtle">
                {{ fulfillmentUpcomingStart | moment('MMM DD') }} to {{
                fulfillmentUpcomingEnd | moment('MMM DD') }}
              </span>
            </template>
          </HeaderBar>
          <v-card-text v-if="gettingFulfillment">
            <v-progress-linear indeterminate />
          </v-card-text>
          <v-card-text v-else>
            <BarChart
              :chart-data="upcomingFulfillment"
              :options="chartOptions"
            />
          </v-card-text>
        </v-card>
      </v-flex>
    </v-layout>

    <v-layout row>
      <v-flex xs12 md12 pa-2>
        <v-tabs>
          <v-tabs-slider></v-tabs-slider>
          <v-tab>
            Employees
          </v-tab>
          <v-tab>
            Top 10 Clients
          </v-tab>
          <v-tab>
            Shift Reviews
          </v-tab>
          <v-tab-item>
            <v-card>
              <!-- redundant with tab title
                <HeaderBar size="headline">
                  Employees
                </HeaderBar>
                -->
              <v-card-text v-if="gettingEmployees">
                <v-progress-linear indeterminate />
              </v-card-text>

              <v-card-text v-else>
                <v-data-table
                  :items="employeesUpcoming"
                  :headers="employeeHeaders"
                  :pagination.sync="employeeSort"
                >
                  <template slot="items" slot-scope="{ item }">
                    <tr>
                      <td>
                        <EmployeeName
                          link
                          showColor
                          :employee="item"
                          :name="item.display_name"
                          :employeeId="item.user_id"
                        />
                        <v-tooltip right>
                          <v-icon
                            class="ml-1"
                            v-if="item.probationary"
                            color="warning"
                            slot="activator"
                          >
                            warning
                          </v-icon>
                          <span>Probationary Standing</span>
                        </v-tooltip>
                      </td>
                      <td>
                        {{ item.avg_last_28 }}
                      </td>
                      <td>
                        {{ item.upcoming_shift_count }}
                      </td>
                      <td>
                        {{ item.upcoming_hours }}
                      </td>
                    </tr>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-tab-item>

          <v-tab-item>
            <v-card>
              <HeaderBar size="headline">
                Top 10 Clients (past 28 days)
                <template slot="buttons">
                  <span class="subtle">
                    <!-- TODO: change to its own date range -->
                    {{ fulfillmentPastStart | moment('MMM DD') }} to {{
                    fulfillmentPastEnd | moment('MMM DD') }}
                  </span>
                </template>
              </HeaderBar>
              <v-card-text v-if="gettingClients">
                <v-progress-linear indeterminate />
              </v-card-text>

              <v-card-text v-else>
                <v-data-table
                  :items="topClients"
                  :headers="clientHeaders"
                  :pagination.sync="clientSort"
                  hide-actions
                >
                  <template slot="items" slot-scope="{ item }">
                    <tr>
                      <td>
                        {{ item.name }}
                      </td>
                      <td>
                        {{ item.shifts_ordered }}
                      </td>
                      <td>
                        {{ item.shifts_fulfilled }}
                      </td>
                      <td>
                        {{ item.fulfillment_rate | decimalPlaces(2) }}%
                      </td>
                    </tr>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-tab-item>

          <v-tab-item>
            <v-card>
              <HeaderBar size="headline">
                Recent Shift Reviews (past 7 days)
                <template slot="buttons">
                  <span class="subtle">
                    Since {{ shiftReviewPastStart | moment('MMM DD') }}
                  </span>
                </template>
              </HeaderBar>
              <v-card-text v-if="gettingShiftReviews">
                <v-progress-linear indeterminate />
              </v-card-text>

              <v-card-text v-else>
                <v-data-table
                  :items="shiftReviews"
                  :headers="shiftReviewHeaders"
                  :pagination.sync="shiftReviewSort"
                  hide-actions
                >
                  <template slot="items" slot-scope="{ item }">
                    <tr>
                      <td>
                        <router-link
                          target="_blank"
                          :to="{ name: 'employeeDetail', params: { employeeId: item.employeeId } }"
                          >{{ item.employeeName }}</router-link
                        >
                      </td>
                      <td>
                        <v-rating
                          half-increments
                          readonly
                          small
                          v-model="item.rating"
                          length="4"
                          ></v-rating>
                      </td>
                      <td>
                        {{ item.createdAt | moment('MMMM Do') }}
                      </td>
                      <td>
                        <router-link
                          target="_blank"
                          :to="{ name: 'clientDetail', params: { clientId: item.reviewerId } }"
                          >{{ item.reviewerName }}</router-link
                        >
                      </td>
                      <td>
                        <router-link
                          target="_blank"
                          :to="{ name: 'clientBusinessDetail', params: { clientBusinessId: item.clientBusinessId } }"
                          >{{ item.location }}</router-link
                        >
                      </td>
                      <td>
                        <router-link
                          target="_blank"
                          :to="{ name: 'shiftDetail', params: { shiftId: item.shiftId } }"
                          >View Shift</router-link
                        >
                      </td>
                    </tr>
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </v-tab-item>
        </v-tabs>
      </v-flex>
    </v-layout>

    <v-layout row wrap mt-2>
      <v-flex xs12 md12 pa-1 pl-0> </v-flex>
    </v-layout>
  </v-container>
</template>

<script>
  import {mapState} from 'vuex';
  import managerApi from '@/api/manager';
  import shiftApi from '@/api/shift';
  import reportsApi from '@/api/reports';
  import AdminTabs from '@/components/dashboard/admin-tabs.vue';

  import BarChart from '@/components/base/bar-chart.vue';

  /*
TODO:
move the fulfillment charts to their own components?
*/

  export default {
    components: {
      AdminTabs,
      shiftApi,
      managerApi,
      BarChart,
    },
    computed: {
      ...mapState('userAuth', ['user']),
    },
    created() {
      this.setUp();
    },

    watch: {},

    data: function() {
      return {
        example: 'test',
        fulfillmentPastStart: this.$moment().subtract(28, 'days').startOf('day'),
        fulfillmentPastEnd: this.$moment().subtract(1, 'days').endOf('day'),
        fulfillmentUpcomingStart: this.$moment().add(1, 'days').startOf('day'),
        fulfillmentUpcomingEnd: this.$moment().add(28, 'days').endOf('day'),
        shiftReviewPastStart: this.$moment().subtract(7, 'days'),
        shiftReviews: [],
        gettingFulfillment: false,
        gettingEmployees: false,
        gettingShiftReviews: false,
        gettingClients: false,
        employeesUpcoming: [],
        employeeSort: {
          sortBy: 'avg_last_28',
          descending: true,
          rowsPerPage: 10,
        },
        employeeHeaders: [
          {
            text: 'Employee',
            value: 'display_name',
            sortable: true,
          },
          {
            text: 'Avg Hs/Week (past 28 days)',
            value: 'avg_last_28',
            sortable: true,
          },
          {
            text: 'Upcoming Shifts',
            value: 'upcoming_shift_count',
            sortable: true,
          },
          {
            text: 'Upcoming Hours',
            value: 'upcoming_hours',
            sortable: true,
          },
        ],
        shiftReviewHeaders: [
          {
            text: 'Employee Name',
            value: 'employeeName',
          },
          {
            text: 'Rating',
            value: 'rating',
          },
          {
            text: 'Reviewed On',
            value: 'createdAt',
          },
          {
            text: 'By',
            value: 'reviewerName',
          },
          {
            text: 'Location',
            value: 'location',
          },
          {
            text: 'Shift',
            value: 'shiftId',
          },
        ],
        shiftReviewSort: {
          sortBy: 'createdAt',
          descending: true,
          rowsPerPage: 10,
        },
        topClients: [],
        clientSort: {
          sortBy: 'shifts_ordered',
          descending: true,
          rowsPerPage: 10,
        },
        clientHeaders: [
          {
            text: 'Client Name',
            value: 'name',
            sortable: true,
          },
          {
            text: 'Shifts Ordered',
            value: 'shifts_ordered',
            sortable: true,
          },
          {
            text: 'Shifts Fulfilled',
            value: 'shifts_fulfilled',
            sortable: true,
          },
          {
            text: 'Fulfillment Rate',
            value: 'fulfillment_rate',
            sortable: true,
          },
        ],
        chartOptions: {
          responsive: true,
          scales: {
            yAxes: [
              {
                ticks: {
                  beginAtZero: true,
                },
              },
            ],
          },
          // scales: {
          //   xAxes: [{
          //     stacked: true
          //   }],
          //   yAxes: [{
          //     stacked: true
          //   }],
          // }
        },
        pastFulfillment: {
          // TODO: figure out how to define colors here and then use object-merging
          //       to take values and labels from API
          //labels: [],
          //datasets: [
          //{
          //label: 'Ordered',
          //backgroundColor: '#f87979',
          //data: [],
          //},
          //{
          //label: 'Fulfilled',
          //backgroundColor: '#9922fa',
          //data: [],
          //},
          //],
        },
        upcomingFulfillment: {},
      };
    },
    methods: {
      getEmployees() {
        let params = {
          managerId: this.user.role == 'manager' ? this.user.id : null,
        };

        this.gettingEmployees = true;
        reportsApi.getEmployeesUpcoming(
          params,
          res => {
            this.employeesUpcoming = res.employees;
            this.gettingEmployees = false;
          },
          error => {
            console.log(error);
            this.gettingEmployees = false;
          },
        );
      },

      getClients() {
        let params = {
          pstart: this.fulfillmentPastStart,
          pend: this.fulfillmentPastEnd,
          managerId: this.user.role == 'manager' ? this.user.id : null,
        };

        this.gettingClients = true;
        try {
          reportsApi.getTopClients(
            params,
            res => {
              this.topClients = res.clients;
              this.gettingClients = false;
            },
            error => {
              console.log(error);
              this.gettingClients = false;
            },
          );
        }
        catch (err) {
          console.log("getClients problem: ", err)
        }
        finally {
          this.gettingClients = false;
        }
      },

      getFulfillment() {
        let params = {
          pstart: this.fulfillmentPastStart,
          pend: this.fulfillmentPastEnd,
          ustart: this.fulfillmentUpcomingStart,
          uend: this.fulfillmentUpcomingEnd,
          managerId: this.user.role == 'manager' ? this.user.id : null,
        };

        this.gettingFulfillment = true;
        reportsApi.getFulfillment(
          params,
          res => {
            this.pastFulfillment = res.past;
            this.upcomingFulfillment = res.upcoming;

            this.gettingFulfillment = false;
          },
          error => {
            console.log(error);
            this.gettingFulfillment = false;
          },
        );
      },

      getShiftReviews() {
        let params = {
          pstart: this.shiftReviewPastStart,
        };

        this.gettingShiftReviews = true;

        reportsApi.getShiftReviews(
          params,
          res => {
            this.shiftReviews = res.shift_reviews;
            this.gettingShiftReviews = false;
          },
          error => {
            console.log(error);
            this.gettingShiftReviews = false;
          },
        );
      },

      setUp() {
        this.getFulfillment();
        this.getEmployees();
        this.getClients();
        this.getShiftReviews();
      },
    },
  };
</script>

<style scoped></style>
